import { useEffect, useMemo, type PropsWithChildren } from 'react'
import { Button } from '@jotta/ui/Button'
import SvgLogoMain from '@jotta/ui/SvgLogo_mainLight'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Trans } from '@lingui/macro'
import { useBrandConfig } from '@jotta/ui/useBrandTheme'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { useDelay } from '@jotta/hooks/useDelay'

const buildAndroidIntentUrl = () => {
  const baseUrl = window.location.href.replace(/^https?:\/\//, '')
  const url = new URL(`intent://${baseUrl}`)
  url.hash = 'Intent;scheme=https;action=android.intent.action.VIEW;end'
  return url.toString()
}

const buildMainDomainLink = (mainDomain: string) => {
  const url = new URL(window.location.href)
  url.hostname = mainDomain
  return url.toString()
}

const buildBreakoutDomainLink = (breakoutDomain: string) => {
  const url = new URL(window.location.href)
  url.hostname = breakoutDomain
  return `x-safari-${url.toString()}`
}

const LandingPage = ({
  isAndroid,
  mainDomain,
}: {
  isAndroid: boolean
  mainDomain: string
}) => {
  const mainDomainLink = buildMainDomainLink(mainDomain)
  const handleOpenInBrowser = () => {
    if (isAndroid) {
      window.location.href = buildAndroidIntentUrl()
    } else {
      window.location.replace(mainDomainLink) // Redirect main domainto attempt opening the app
    }
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(mainDomainLink)
  }

  return (
    <main className="flex h-full w-full items-center justify-center">
      <div className="flex min-h-full w-full flex-col items-center justify-center p-8 sm:min-h-[500px] sm:max-w-[536px]">
        <div className="flex flex-grow flex-col place-items-center justify-center">
          <PlainBrandIcon icon={SvgLogoMain} height={55} />
          <p className="text-pretty pt-6 text-center">
            <Trans>
              For better security and a better overall experience, please open
              this link outside of this application.
            </Trans>
          </p>
        </div>
        <div className="flex w-full flex-col items-center justify-center py-4 sm:max-w-[536px]">
          <Button
            className="btn btn-primary flex w-full justify-center"
            onClick={handleOpenInBrowser}
          >
            <Trans>Continue</Trans>
          </Button>
          <p className="text-pretty pt-6 text-center">
            <Trans>
              Having trouble?{' '}
              <a
                className="btn-link"
                onClick={handleCopyLink}
                href={mainDomainLink}
              >
                Copy this link
              </a>{' '}
              and paste into your favorite browser.
            </Trans>
          </p>
        </div>
      </div>
    </main>
  )
}

export const InAppBrowserBreakout = ({ children }: PropsWithChildren) => {
  const { breakoutDomain, mainDomain } = useBrandConfig()

  const { isAndroid, isIOS, isBreakoutDomain, isInAppBrowser } = useMemo(() => {
    const ua = navigator.userAgent
    return {
      isAndroid: /android/i.test(ua),
      isIOS: /iPad|iPhone|iPod/.test(ua),
      isBreakoutDomain: window.location.hostname === breakoutDomain,
      isInAppBrowser:
        /Instagram|FBAN|FBAV|Messenger|Line|Snapchat|Twitter|WeChat|TikTok/.test(
          ua,
        ),
    }
  }, [breakoutDomain])

  // For all paths in iOS in-app browsers, force breakout to Safari
  const shouldBreakOut = isIOS && isInAppBrowser

  // Add a loading spinner to avoid flickering when auto-navigating to and from the breakout domain
  const isLoading = useDelay({
    initialValue: shouldBreakOut || isBreakoutDomain,
    value: false,
    delayMs: 150,
  })

  useEffect(() => {
    if (isBreakoutDomain && shouldBreakOut) {
      return
    } else if (isBreakoutDomain) {
      // If on the breakout domain, redirect immediately to the main domain
      window.location.replace(buildMainDomainLink(mainDomain))
    } else if (shouldBreakOut) {
      window.location.href = buildBreakoutDomainLink(breakoutDomain)
    }
  }, [isBreakoutDomain, mainDomain, shouldBreakOut, breakoutDomain])

  if (isInAppBrowser || isBreakoutDomain) {
    return isLoading ? (
      <LoadingOverlay open />
    ) : (
      <LandingPage isAndroid={isAndroid} mainDomain={mainDomain} />
    )
  } else {
    return children
  }
}
